import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Box,
  CssBaseline,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Checkbox,
  Slider,
  Grid,
  Stack,
  Tooltip,
  Avatar,
  Alert,
  AlertTitle,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { useDispatch, useSelector } from "react-redux";
import {
  CustomTableContainer,
  tableCellStyle,
  CustomTableHead,
} from "../../../components/TableComponent";
import {
  addCandidate,
  removeCandidate,
  clearCandidates,
} from "../../../store/reducers/appreducer";
import {
  fetchRankingsAPI,
  generatePeerToPeerReportAPI,
  selectCandidatesForInterviewAPI,
  updateCandidateEmailAPI,
  downloadResumeAPI,
} from "../../../services/resumeService";
import { startBatchCallAPI, getBatchStatusAPI } from "../../../services/interviewService";
import { scheduleScreeningCallAPI } from "../../../services/screeningService";
import PhoneIcon from "@mui/icons-material/Phone";
import PeerReport from "./PeerReport";
import { paths } from "../../../routes/paths";
import { exportToCsv } from "../../../utils/exportCsv";

const Report = ({ setSelectedIndex }) => {
  const navigate = useNavigate();
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const state = useSelector((state) => state);
  const selectedCandidates = useSelector(
    (state) => state.appreducer?.selectedCandidates || []
  );

  const dispatch = useDispatch();

  const [editingIndex, setEditingIndex] = useState(null);
  const [currentEmail, setCurrentEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [sliderValue, setSliderValue] = useState(0);
  const tableRef = useRef(null);
  const scrollPosition = useRef({ top: 0, left: 0 });
  const { jobId } = useParams();

  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchStatus, setBatchStatus] = useState(null);
  const [screeningLoading, setScreeningLoading] = useState(false);

  const saveScrollPosition = () => {
    if (tableRef.current) {
      scrollPosition.current = {
        top: tableRef.current.scrollTop,
        left: tableRef.current.scrollLeft,
      };
    }
  };
  const restoreScrollPosition = () => {
    if (tableRef.current) {
      tableRef.current.scrollTop = scrollPosition.current.top;
      tableRef.current.scrollLeft = scrollPosition.current.left;
    }
  };
  useEffect(() => {
    restoreScrollPosition();
  }, [selectedCandidates, editingIndex]);

  const fetchRankings = async () => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const rankings = await fetchRankingsAPI(jobId);

      if (!rankings.error) {
        setRankings(rankings);
      } else {
        setError(rankings.error);
        dispatch(
          showAppAlert({
            show: true,
            alertType: "error",
            alertMessage: rankings.error || "Failed to Fetch Rankings",
          })
        );
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const generatePeerToPeerReport = async (topK) => {
    try {
      setLoading(true);
      await generatePeerToPeerReportAPI(jobId, topK);
      await fetchRankings();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = (topK) => {
    generatePeerToPeerReport(topK);
  };

  useEffect(() => {
    fetchRankings();
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;
    const checkBatch = async () => {
      try {
        const status = await getBatchStatusAPI(jobId);
        setBatchStatus(status);
      } catch {}
    };
    checkBatch();
    const interval = setInterval(checkBatch, 10000);
    return () => clearInterval(interval);
  }, [jobId]);

  const handleStartBatchCalls = async () => {
    setBatchLoading(true);
    try {
      const resumeIds = selectedCandidates.map((c) => c.ResumeId);
      const result = await startBatchCallAPI(jobId, resumeIds);
      setBatchStatus({ active: true, current: 1, total: result.total, status: "running" });
      setBatchDialogOpen(false);
      dispatch(showAppAlert({ show: true, alertType: "success", alertMessage: result.message }));
    } catch (e) {
      dispatch(showAppAlert({ show: true, alertType: "error", alertMessage: e.message }));
    } finally {
      setBatchLoading(false);
    }
  };

  const selectedCandidatesMissingPhone = selectedCandidates.filter((sc) => {
    const ranking = rankings.find((r) => r["Resume Id"] === sc.ResumeId);
    const info = ranking?.["Candidate Info"] || {};
    return !(info.phone || info.Phone);
  });

  // Blocks re-scheduling screening (avoids duplicate ScreeningCall attempts) but does
  // NOT block interview scheduling — the two are independent.
  const selectedCandidatesAlreadyScreened = selectedCandidates.filter((sc) => {
    const ranking = rankings.find((r) => r["Resume Id"] === sc.ResumeId);
    return ranking?.["Screening Scheduled"] === true;
  });

  // Mirrors the screening check — blocks re-scheduling an interview, does NOT block screening.
  const selectedCandidatesAlreadyInterviewed = selectedCandidates.filter((sc) => {
    const ranking = rankings.find((r) => r["Resume Id"] === sc.ResumeId);
    return ranking?.["Interview Selected"] === true;
  });

  // Single consolidated list for the selection-issues banner instead of one row per issue type.
  const selectionWarnings = [
    selectedCandidatesMissingPhone.length > 0 &&
      `Screening can't be scheduled — ${selectedCandidatesMissingPhone.map((c) => c.name || c.ResumeId).join(", ")} ${selectedCandidatesMissingPhone.length === 1 ? "has" : "have"} no phone number.`,
    selectedCandidatesAlreadyScreened.length > 0 &&
      `Screening already scheduled for: ${selectedCandidatesAlreadyScreened.map((c) => c.name || c.ResumeId).join(", ")}`,
    selectedCandidatesAlreadyInterviewed.length > 0 &&
      `Interview already scheduled for: ${selectedCandidatesAlreadyInterviewed.map((c) => c.name || c.ResumeId).join(", ")}`,
  ].filter(Boolean);

  const handleScheduleScreeningCalls = async () => {
    setScreeningLoading(true);
    try {
      const resumeIds = selectedCandidates.map((c) => c.ResumeId);
      const outcomes = await Promise.allSettled(
        resumeIds.map((resumeId) => scheduleScreeningCallAPI(jobId, resumeId))
      );
      const successCount = outcomes.filter((o) => o.status === "fulfilled").length;
      const failureCount = outcomes.length - successCount;

      if (successCount > 0) {
        dispatch(
          showAppAlert({
            show: true,
            alertType: failureCount > 0 ? "warning" : "success",
            alertMessage:
              `Screening scheduled for ${successCount} candidate${successCount === 1 ? "" : "s"}.` +
              (failureCount > 0 ? ` ${failureCount} failed.` : ""),
          })
        );
        navigate(`${paths.dashboard.screening}/${jobId}`);
      } else {
        dispatch(
          showAppAlert({
            show: true,
            alertType: "error",
            alertMessage: "Failed to schedule screening calls for the selected candidates.",
          })
        );
      }
    } catch (error) {
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: error.message || "Failed to schedule screening calls.",
        })
      );
    } finally {
      setScreeningLoading(false);
    }
  };

  const handleEditClick = (index, candidateInfo) => {
    setEditingIndex(index);
    setCurrentEmail(candidateInfo.Email || candidateInfo.email || "");
    setOpenDialog(true);
    setEmailError("");
  };

  const handleSaveClick = async (index, candidateId) => {
    saveScrollPosition();

    if (!validateEmail(currentEmail)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    try {
      await updateCandidateEmailAPI(jobId, candidateId, currentEmail);
      console.log(currentEmail);

      await fetchRankings();

      dispatch(
        showAppAlert({
          show: true,
          alertType: "success",
          alertMessage: "Email updated successfully",
        })
      );

      setOpenDialog(false);
      setEditingIndex(null);
    } catch (error) {
      console.error("Failed to update email:", error);
      setEmailError(error.message);
    }
  };

  const handleCancel = () => {
    setOpenDialog(false);
  };

  const handleEmailChange = (e) => {
    setCurrentEmail(e.target.value);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleStartInterview = async () => {
    try {
      setLoading(true);
      // Schedule one at a time — sending the whole selection in a single bulk PUT
      // was only reliably taking effect for one candidate.
      const outcomes = await Promise.allSettled(
        selectedCandidates.map((candidate) =>
          selectCandidatesForInterviewAPI(jobId, [candidate])
        )
      );
      const successCount = outcomes.filter((o) => o.status === "fulfilled").length;
      const failureCount = outcomes.length - successCount;

      if (successCount > 0) {
        dispatch(
          showAppAlert({
            show: true,
            alertType: failureCount > 0 ? "warning" : "success",
            alertMessage:
              `Interview scheduled for ${successCount} candidate${successCount === 1 ? "" : "s"}.` +
              (failureCount > 0 ? ` ${failureCount} failed.` : ""),
          })
        );
        navigate(`${paths.dashboard.interviews}/${jobId}`);
      } else {
        dispatch(
          showAppAlert({
            show: true,
            alertType: "error",
            alertMessage: "Failed to schedule interviews for the selected candidates.",
          })
        );
      }
    } catch (error) {
      console.error("Error selecting candidates:", error);
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: error.message,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCandidateSelect = (ResumeId, name, rank) => (event) => {
    saveScrollPosition();

    if (event.target.checked) {
      dispatch(addCandidate({ ResumeId, name, rank }));
    } else {
      dispatch(removeCandidate({ ResumeId }));
    }
  };

  const selectableCandidates = rankings.filter(
    (ranking) => !ranking["Interview Selected"]
  );
  const totalSelectableCandidates = selectableCandidates.length;

  const handleSliderChange = (event, newValue) => {
    saveScrollPosition();
    setSliderValue(newValue);
    const numberOfCandidatesToSelect = Math.round(
      (newValue / 100) * totalSelectableCandidates
    );

    const candidatesToSelect = selectableCandidates
      .slice(0, numberOfCandidatesToSelect)
      .map((ranking) => ({
        ResumeId: ranking["Resume Id"],
        name:
          ranking["Candidate Info"]?.Name || ranking["Candidate Info"]?.name,
        rank: ranking["Resume Rank"],
      }));
    dispatch(clearCandidates());
    candidatesToSelect.forEach((candidate) => {
      dispatch(addCandidate(candidate));
    });
  };

  // Helper functions
  const getInitials = (name) => {
    if (!name) return "N/A";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatScore = (score) => {
    return score ? parseFloat(score).toFixed(1) : "N/A";
  };

  // New handler functions for top selection buttons
  const handleSelectTop = (count) => {
    const candidatesToSelect = selectableCandidates
      .slice(0, count)
      .map((ranking) => ({
        ResumeId: ranking["Resume Id"],
        name:
          ranking["Candidate Info"]?.Name || ranking["Candidate Info"]?.name,
        rank: ranking["Resume Rank"],
      }));
    dispatch(clearCandidates());
    candidatesToSelect.forEach((candidate) => {
      dispatch(addCandidate(candidate));
    });
  };

  const handleClearSelection = () => {
    dispatch(clearCandidates());
  };

  const handleExport = () => {
    const rows = rankings.map((ranking) => {
      const info = ranking["Candidate Info"] || {};
      const params = ranking.Parameters || {};
      return [
        ranking["Resume Rank"] ?? "",
        info.Name || info.name || "N/A",
        info.Email || info.email || "",
        ranking["Total Score"] != null ? Number(ranking["Total Score"]).toFixed(1) : "",
        params.achievements_score ?? "",
        params.education_score ?? "",
        params.relevance_score ?? "",
        params.skills_score ?? "",
        params.work_experience_score ?? "",
        ranking["p2p_score"] ?? "",
        ranking["Interview Selected"] ? "Yes" : "No",
        ranking["Screening Scheduled"] ? "Yes" : "No",
      ];
    });
    exportToCsv(
      `resume-dashboard-${jobId}.csv`,
      [
        "Rank",
        "Candidate Name",
        "Email",
        "Total Score",
        "Achievements",
        "Education",
        "Relevance",
        "Skills",
        "Experience",
        "P2P Score",
        "Interview Selected",
        "Screening Scheduled",
      ],
      rows
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!jobId) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CssBaseline />
        <Typography variant="h5">
          Select a job from Job Management Section
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: "1px solid rgba(0, 0, 0, 0.2)",
        borderRadius: "30px",
        background: "#FFFFFF",
        boxShadow: "6px 6px 54px 0px #0000000D",
        padding: "20px",
      }}
    >
      {/* Header Section */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "#1f2937", mb: 0.5 }}
          >
            Candidate Analysis
          </Typography>
          <Typography variant="body1" sx={{ color: "#6b7280" }}>
            {rankings.length} candidates found
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography
            variant="body1"
            sx={{ color: "#6b7280", fontWeight: 500 }}
          >
            {selectedCandidates.length} selected
          </Typography>
          <Tooltip
            title={
              selectedCandidates.length > 0 && selectedCandidatesMissingPhone.length > 0
                ? `Screening can't be scheduled — ${selectedCandidatesMissingPhone.map((c) => c.name || c.ResumeId).join(", ")} ${selectedCandidatesMissingPhone.length === 1 ? "has" : "have"} no phone number.`
                : selectedCandidates.length > 0 && selectedCandidatesAlreadyScreened.length > 0
                ? `Screening already scheduled for: ${selectedCandidatesAlreadyScreened.map((c) => c.name || c.ResumeId).join(", ")}`
                : ""
            }
          >
            <span>
              <Button
                variant="contained"
                startIcon={<PhoneIcon />}
                sx={{
                  backgroundColor: "#7c3aed",
                  "&:hover": { backgroundColor: "#6d28d9" },
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                }}
                onClick={handleScheduleScreeningCalls}
                disabled={
                  selectedCandidates.length === 0 ||
                  screeningLoading ||
                  selectedCandidatesMissingPhone.length > 0 ||
                  selectedCandidatesAlreadyScreened.length > 0
                }
              >
                {screeningLoading ? "Scheduling..." : `Schedule Screening (${selectedCandidates.length})`}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              selectedCandidates.length > 0 && selectedCandidatesAlreadyInterviewed.length > 0
                ? `Interview already scheduled for: ${selectedCandidatesAlreadyInterviewed.map((c) => c.name || c.ResumeId).join(", ")}`
                : ""
            }
          >
            <span>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#2563eb",
                  "&:hover": { backgroundColor: "#1d4ed8" },
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                }}
                onClick={handleStartInterview}
                disabled={
                  selectedCandidates.length === 0 ||
                  selectedCandidatesAlreadyInterviewed.length > 0
                }
              >
                Schedule Interviews ({selectedCandidates.length})
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<PhoneIcon />}
            sx={{
              backgroundColor: "#16a34a",
              "&:hover": { backgroundColor: "#15803d" },
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1,
            }}
            onClick={() => setBatchDialogOpen(true)}
            disabled={selectedCandidates.length === 0 || batchStatus?.active}
          >
            {batchStatus?.active
              ? `Calling ${batchStatus.current}/${batchStatus.total}...`
              : `Start AI Calls (${selectedCandidates.length})`}
          </Button>
        </Box>
      </Box>

      {selectedCandidates.length > 0 && selectionWarnings.length > 0 && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{
            mb: 2,
            mt: -1,
            borderRadius: 2,
            borderColor: "#fbbf24",
            bgcolor: "#fffbeb",
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <AlertTitle sx={{ fontWeight: 600, fontSize: "14px" }}>
            {selectionWarnings.length === 1 ? "1 action needed" : `${selectionWarnings.length} actions needed`}
          </AlertTitle>
          <Stack spacing={0.5}>
            {selectionWarnings.map((message, i) => (
              <Typography key={i} variant="body2" sx={{ color: "#92400e" }}>
                {message}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {rankings.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="150px"
        >
          <Typography variant="body1" align="center">
            No Resumes Uploaded for this Job. Please upload resume first.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Selection Buttons and Peer Rankings in the same row */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={() => handleSelectTop(5)}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  borderColor: "#d1d5db",
                  color: "#374151",
                  "&:hover": {
                    borderColor: "#9ca3af",
                    backgroundColor: "#f9fafb",
                  },
                }}
              >
                Select Top 5
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleSelectTop(10)}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  borderColor: "#d1d5db",
                  color: "#374151",
                  "&:hover": {
                    borderColor: "#9ca3af",
                    backgroundColor: "#f9fafb",
                  },
                }}
              >
                Select Top 10
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearSelection}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  borderColor: "#d1d5db",
                  color: "#374151",
                  "&:hover": {
                    borderColor: "#9ca3af",
                    backgroundColor: "#f9fafb",
                  },
                }}
              >
                Clear Selection
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                disabled={rankings.length === 0}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  borderColor: "#d1d5db",
                  color: "#374151",
                  "&:hover": {
                    borderColor: "#9ca3af",
                    backgroundColor: "#f9fafb",
                  },
                }}
              >
                Export
              </Button>
            </Box>
            <Box>
              <PeerReport
                rankingsLength={rankings.length}
                onGenerate={handleGenerate}
              />
            </Box>
          </Box>

          {/* Table */}
          <Box
            sx={{
              borderRadius: "16px",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              backgroundColor: "#fff",
              margin: "16px 0",
            }}
          >
            <CustomTableContainer 
              ref={tableRef} 
              component={Paper}
              sx={{
                "&::-webkit-scrollbar": {
                  width: "0px",
                  height: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#d1d5db",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#9ca3af",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <Table sx={{ minWidth: 'auto' }}>
                <CustomTableHead
                  style={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#374151",
                    zIndex: 1,
                  }}
                >
                  <TableRow>
                    <TableCell
                      style={{
                        ...tableCellStyle,
                        width: "50px",
                        fontWeight: 600,
                        color: "#ffffff",
                        backgroundColor: "#374151",
                      }}
                    >
                      ✓
                    </TableCell>
                    <TableCell
                      style={{
                        ...tableCellStyle,
                        width: "60px",
                        fontWeight: 600,
                        color: "#ffffff",
                        backgroundColor: "#374151",
                      }}
                    >
                      Rank
                    </TableCell>
                    <TableCell
                      style={{
                        ...tableCellStyle,
                        width: "200px",
                        fontWeight: 600,
                        color: "#ffffff",
                        backgroundColor: "#374151",
                      }}
                    >
                      Candidate ↕
                    </TableCell>
                    <TableCell
                      style={{
                        ...tableCellStyle,
                        width: "80px",
                        fontWeight: 600,
                        color: "#ffffff",
                        backgroundColor: "#374151",
                      }}
                    >
                      Total Score ↕
                    </TableCell>
                    <TableCell
                      style={{
                        ...tableCellStyle,
                        width: "80px",
                        fontWeight: 600,
                        color: "#ffffff",
                        backgroundColor: "#374151",
                      }}
                    >
                      Achievements
                    </TableCell>
                    <TableCell
                      style={{
                        ...tableCellStyle,
                        width: "80px",
                        fontWeight: 600,
                        color: "#ffffff",
                        backgroundColor: "#374151",
                      }}
                    >
                      Education
                    </TableCell>
                    <TableCell
                      style={{
                        ...tableCellStyle,
                        width: "80px",
                        fontWeight: 600,
                        color: "#ffffff",
                        backgroundColor: "#374151",
                      }}
                    >
                      Relevance
                    </TableCell>
                    <TableCell
                      style={{
                        ...tableCellStyle,
                        width: "80px",
                        fontWeight: 600,
                        color: "#ffffff",
                        backgroundColor: "#374151",
                      }}
                    >
                      Skills ↕
                    </TableCell>
                    <TableCell
                      style={{
                        ...tableCellStyle,
                        width: "80px",
                        fontWeight: 600,
                        color: "#ffffff",
                        backgroundColor: "#374151",
                      }}
                    >
                      Experience
                    </TableCell>
                    <TableCell
                      style={{
                        ...tableCellStyle,
                        width: "80px",
                        fontWeight: 600,
                        color: "#ffffff",
                        backgroundColor: "#374151",
                      }}
                    >
                      P2P Score
                    </TableCell>
                    <TableCell
                      style={{
                        ...tableCellStyle,
                        width: "80px",
                        fontWeight: 600,
                        color: "#ffffff",
                        backgroundColor: "#374151",
                      }}
                    >
                      P2P Rank
                    </TableCell>
                    <TableCell
                      style={{
                        ...tableCellStyle,
                        width: "120px",
                        fontWeight: 600,
                        color: "#ffffff",
                        backgroundColor: "#374151",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </CustomTableHead>
                <TableBody>
                  {rankings.map((ranking, index) => {
                    const candidateInfo = ranking["Candidate Info"] || {};
                    const ResumeId = ranking["Resume Id"];
                    const rank = ranking["Resume Rank"];
                    const candidateName =
                      candidateInfo.Name || candidateInfo.name || "N/A";
                    const candidateEmail =
                      candidateInfo.Email || candidateInfo.email || "";
                    const isAlreadySelected = ranking["Interview Selected"] === true;

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f8f9fa",
                          },
                          borderBottom: "1px solid #e5e7eb",
                          backgroundColor: "transparent",
                        }}
                      >
                        <TableCell sx={{ padding: "16px" }}>
                          <Checkbox
                            checked={
                              Array.isArray(selectedCandidates) &&
                              selectedCandidates.some(
                                (candidate) => candidate.ResumeId === ResumeId
                              )
                            }
                            onChange={handleCandidateSelect(
                              ResumeId,
                              candidateName,
                              rank
                            )}
                            sx={{
                              color: "#6b7280",
                              "&.Mui-checked": {
                                color: "#2563eb",
                              },
                              "&.Mui-disabled": {
                                color: "#d1d5db",
                              },
                            }}
                          />
                        </TableCell>

                        <TableCell
                          sx={{ padding: "16px", textAlign: "center" }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: "#1f2937",
                              fontSize: "18px",
                            }}
                          >
                            #{rank}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ padding: "16px" }}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar
                              sx={{
                                bgcolor: "#3b82f6",
                                color: "#ffffff",
                                width: 48,
                                height: 48,
                                fontSize: "16px",
                                fontWeight: 600,
                              }}
                            >
                              {getInitials(candidateName)}
                            </Avatar>
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 600,
                                  color: "#1f2937",
                                  cursor: "pointer",
                                  "&:hover": {
                                    color: "#2563eb",
                                    textDecoration: "underline",
                                  },
                                }}
                                onClick={() =>
                                  window.open(
                                    `/candidate/${jobId}/${ResumeId}`,
                                    "_blank"
                                  )
                                }
                              >
                                {candidateName}
                                {isAlreadySelected && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      ml: 1,
                                      px: 1,
                                      py: 0.5,
                                      bgcolor: "#dbeafe",
                                      color: "#2563eb",
                                      borderRadius: "12px",
                                      fontSize: "10px",
                                      fontWeight: 500,
                                    }}
                                  >
                                    INTERVIEW
                                  </Typography>
                                )}
                                {ranking["Screening Scheduled"] && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      ml: 1,
                                      px: 1,
                                      py: 0.5,
                                      bgcolor: "#ede9fe",
                                      color: "#7c3aed",
                                      borderRadius: "12px",
                                      fontSize: "10px",
                                      fontWeight: 500,
                                    }}
                                  >
                                    SCREENING
                                  </Typography>
                                )}
                              </Typography>
                              {candidateEmail && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#6b7280",
                                    fontSize: "12px",
                                  }}
                                >
                                  {candidateEmail}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell
                          sx={{ padding: "16px", textAlign: "center" }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: "#1f2937",
                              fontSize: "16px",
                            }}
                          >
                            {formatScore(ranking["Total Score"])}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{ padding: "16px", textAlign: "center" }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#374151",
                              fontSize: "14px",
                            }}
                          >
                            {formatScore(ranking.Parameters.achievements_score)}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{ padding: "16px", textAlign: "center" }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#374151",
                              fontSize: "14px",
                            }}
                          >
                            {formatScore(ranking.Parameters.education_score)}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{ padding: "16px", textAlign: "center" }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#374151",
                              fontSize: "14px",
                            }}
                          >
                            {formatScore(ranking.Parameters.relevance_score)}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{ padding: "16px", textAlign: "center" }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#374151",
                              fontSize: "14px",
                            }}
                          >
                            {formatScore(ranking.Parameters.skills_score)}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{ padding: "16px", textAlign: "center" }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#374151",
                              fontSize: "14px",
                            }}
                          >
                            {formatScore(ranking.Parameters.work_experience_score)}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{ padding: "16px", textAlign: "center" }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#374151",
                              fontSize: "14px",
                            }}
                          >
                            {formatScore(ranking["p2p_score"])}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{ padding: "16px", textAlign: "center" }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#374151",
                              fontSize: "14px",
                            }}
                          >
                            {ranking["p2p_rank"] || "None"}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ padding: "16px" }}>
                          <Box display="flex" gap={1} justifyContent="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                sx={{
                                  backgroundColor: "#f3f4f6",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "8px",
                                  color: "#374151",
                                  "&:hover": {
                                    backgroundColor: "#e5e7eb",
                                  },
                                }}
                                onClick={() =>
                                  window.open(
                                    `/candidate/${jobId}/${ResumeId}`,
                                    "_blank"
                                  )
                                }
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Resume">
                              <IconButton
                                size="small"
                                sx={{
                                  backgroundColor: "#f3f4f6",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "8px",
                                  color: "#374151",
                                  "&:hover": {
                                    backgroundColor: "#e5e7eb",
                                  },
                                }}
                                onClick={async () => {
                                  try {
                                    const url = await downloadResumeAPI(jobId, ResumeId);
                                    window.open(url, "_blank");
                                  } catch (e) {
                                    dispatch(showAppAlert({ show: true, alertType: "error", alertMessage: "Failed to download resume" }));
                                  }
                                }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Email">
                              <IconButton
                                size="small"
                                sx={{
                                  backgroundColor: "#f3f4f6",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "8px",
                                  color: "#374151",
                                  "&:hover": {
                                    backgroundColor: "#e5e7eb",
                                  },
                                }}
                                onClick={() =>
                                  handleEditClick(index, candidateInfo)
                                }
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CustomTableContainer>
          </Box>

          {/* Slider component */}
          {rankings.length > 0 && (
            <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <Slider
                  value={sliderValue}
                  onChange={handleSliderChange}
                  aria-labelledby="candidate-slider"
                  valueLabelDisplay="auto"
                  step={5}
                  min={0}
                  max={100}
                  disabled={totalSelectableCandidates === 0}
                  sx={{
                    color: "#2563eb",
                    "& .MuiSlider-thumb": {
                      backgroundColor: "#2563eb",
                    },
                    "& .MuiSlider-track": {
                      backgroundColor: "#2563eb",
                    },
                    "& .MuiSlider-rail": {
                      backgroundColor: "#e5e7eb",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {totalSelectableCandidates === 0 ? (
                  <Typography variant="body1" gutterBottom>
                    No Candidates To Select
                  </Typography>
                ) : (
                  <Typography
                    variant="body1"
                    gutterBottom
                    sx={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#374151",
                    }}
                  >
                    Selected Candidates: {sliderValue}% from available
                    candidates
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}
        </>
      )}

      {error && <Typography color="error">{error}</Typography>}

      {/* Email Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "12px",
            border: "2px solid #D5D5D5",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <DialogTitle>Edit Email</DialogTitle>
        <DialogContent>
          <TextField
            variant="outlined"
            label="Email ID"
            value={currentEmail}
            onChange={handleEmailChange}
            fullWidth
            InputProps={{
              style: {
                backgroundColor: "white",
                fontWeight: "bold",
                fontSize: "medium",
                color: "#555",
                background: "#fff",
                padding: "0 4px",
              },
            }}
            InputLabelProps={{
              style: {
                color: "#555",
                padding: "8px",
              },
            }}
            error={!!emailError}
            helperText={emailError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() =>
              handleSaveClick(editingIndex, rankings[editingIndex]["Resume Id"])
            }
            color="primary"
            variant="contained"
            disabled={!currentEmail}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Call Confirmation Dialog */}
      <Dialog open={batchDialogOpen} onClose={() => setBatchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Start AI Phone Interviews
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The AI will call <strong>{selectedCandidates.length}</strong> candidate(s) one by one and conduct a phone interview with each.
          </Typography>
          <Box sx={{ bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 2, p: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ color: "#166534", fontWeight: 600, mb: 1 }}>
              How it works
            </Typography>
            <Typography variant="body2" sx={{ color: "#166534" }}>
              • First candidate gets called immediately<br />
              • After each interview finishes, the next candidate is called automatically<br />
              • Interview scores appear in the Rankings tab once complete
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Make sure all selected candidates have phone numbers. Candidates without a number will be skipped.
          </Typography>
          <Box sx={{ mt: 2, maxHeight: 160, overflowY: "auto" }}>
            {selectedCandidates.map((c) => (
              <Box key={c.ResumeId} sx={{ display: "flex", alignItems: "center", py: 0.5, gap: 1 }}>
                <PhoneIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                <Typography variant="body2">{c.name || "Unknown"}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBatchDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStartBatchCalls}
            disabled={batchLoading}
            startIcon={batchLoading ? <CircularProgress size={16} /> : <PhoneIcon />}
            sx={{
              backgroundColor: "#16a34a",
              "&:hover": { backgroundColor: "#15803d" },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {batchLoading ? "Starting..." : `Call ${selectedCandidates.length} Candidates`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Report;
